import { UserId } from '@business/entities/account';
import { ContactActivityResponse, ContactId, ContactResponse, ContactActivityType } from '@business/entities/contact';
import { OrgId } from '@business/entities/organization';
import { CommonService, ContactService } from '@business/services';
import { ActivityService } from '@business/services/activity_service';
import { FormSelect, FormSelectItem } from '@core/components/form';
import { LoadingIndicator } from '@core/components/LoadingIndicator';
import { useBloc } from '@core/utils/bloc';
import { formControl, FormDataType, formGroup } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { R } from '@core/utils/r';
import { useLoadingState } from '@core/utils/repository/loading_state';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Col, DropdownMenu, DropdownToggle, Media, Row, UncontrolledDropdown } from 'reactstrap';
import { openEditNotesModal } from './EditNotesModal';
import { EmailTimeLineItem } from './EmailTimeLineItem';
import { NotesTimelineBloc } from './NotesTimelineBloc';
import { TicketTimelineItem } from './TicketTimelineItem';
import { switchMap, tap } from 'rxjs/operators';
import { EventMeta } from '@business/entities/calendar';

interface NoteReferenceItemProps {
  item: ContactActivityResponse;
  orgId: OrgId;
  contacts: ContactResponse[];
  onDelete(id: string): void;
  onTogglePin(id: string): void;
}

export const NoteReferenceItem = memo((props: NoteReferenceItemProps) => {
  const { item, contacts, orgId, onDelete, onTogglePin } = props;
  return (
    <Media>
      <div className="mr-3">
        <i className={'bx ' + ContactService.getActivityIcon(item.reference.type) + ' h4 text-primary'} />
      </div>
      <Media body>
        <div className="bg-light p-3">
          {/* <h5 className="font-size-15">
          <Link to="#" className="text-dark">
            {item.reference.message}
          </Link>
        </h5> */}
          <div className="d-flex justify-content-between">
            <span className="text-primary">
              {CommonService.getFullName(item.user)} &gt; {item.contactName}
            </span>
            <span className="text-primary d-flex">
              {Formatter.formatTime(item.reference.createdAt, 'LLL')}
              <a onClick={() => onTogglePin(item.id)} className="ml-2 mr-1">
                {!item.pinnedAt && <i className="bx bx-pin font-size-14"></i>}
                {!!item.pinnedAt && <i className="bx bxs-pin font-size-14"></i>}
              </a>
              <UncontrolledDropdown className="ml-2">
                <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center text-dark">
                  <i className="bx bx-dots-horizontal-rounded"></i>
                </DropdownToggle>
                <DropdownMenu right>
                  <a
                    className="dropdown-item"
                    onClick={() =>
                      openEditNotesModal({
                        contactId: item.contactId,
                        contacts: contacts,
                        note: item,
                        orgId,
                      })
                    }
                  >
                    Edit
                  </a>
                  <div className="dropdown-divider"></div>
                  <a className="dropdown-item text-danger" onClick={() => onDelete(item.id)}>
                    Delete
                  </a>
                </DropdownMenu>
              </UncontrolledDropdown>
            </span>
          </div>
          {!!item.reference.forDate && <p className="text-primary">For Date: {Formatter.formatTime(item.reference.forDate, 'LLL')}</p>}
          <div dangerouslySetInnerHTML={{ __html: item.reference.result || '' }}></div>
          <div dangerouslySetInnerHTML={{ __html: item.reference.message }}></div>
        </div>
      </Media>
    </Media>
  );
});

function defineTimelineForm() {
  return formGroup({
    users: formControl<UserId[]>({
      placeholder: 'Users',
    }),
    contacts: formControl<ContactId[]>({
      placeholder: 'Contacts',
    }),
    types: formControl<string[]>({
      placeholder: 'Type',
    }),
  });
}

export type TimelineFormData = FormDataType<typeof defineTimelineForm>;

export interface NotesTimelineProps {
  orgId?: OrgId;
  loadContacts?: boolean;
  type?: 'project' | 'deal' | 'order' | 'subscription';
  showAll?: boolean;
  objectId?: string;
  contactId?: ContactId;
  loadOrg?: boolean;
  showFilters?: boolean;
  selectedNoteType?: ContactActivityType;
  scrollableDiv?: string | null;
}

export const NotesTimeline = memo((props: NotesTimelineProps) => {
  const {
    orgId,
    loadContacts = true,
    type,
    objectId,
    contactId,
    loadOrg = true,
    showFilters = true,
    selectedNoteType,
    showAll = true,
    scrollableDiv = null,
  } = props;
  const form = useMemo(() => defineTimelineForm(), []);
  const formValue = useObservable(form.value$);
  const bloc = useBloc(NotesTimelineBloc, orgId, loadContacts, type, objectId, contactId, loadOrg, showAll);
  const contacts = useObservable(bloc.contacts$, []);
  const users = useObservable(bloc.users$, []);
  const activities = useObservable(bloc.activities$, []);
  const hasMore = useNonNilObservable(bloc.hasMore$);
  const loading = useLoadingState(bloc.loading);
  const [queryPage, setQueryPage] = useState<number>(1);

  const userItems = useMemo(() => CommonService.selectItems(users, u => CommonService.getFullName(u), 'id', true), [users]);
  const contactItems = useMemo(() => CommonService.selectItems(contacts, u => CommonService.getFullName(u), 'id', true), [contacts]);
  const typeItems = useMemo(() => CommonService.selectItems(ActivityService.getTypes(), t => ActivityService.getTypeTitle(t)), []);

  const onDelete = useCallback(
    (id: string) => {
      bloc.deleteActivity(id).subscribe();
    },
    [bloc],
  );
  const onTogglePin = useCallback(
    (id: string) => {
      bloc.onTogglePin(id).subscribe();
    },
    [bloc],
  );

  const onTogglePinEmail = useCallback(
    (id: string) => {
      bloc
        .onTogglePinEmail(id)
        .pipe(tap(() => bloc.onReload(1)))
        .subscribe();
    },
    [bloc],
  );

  useEffect(() => {
    if (formValue?.contacts || formValue?.types || formValue?.users) {
      bloc.filters$.next(formValue);
      bloc.onReload(1);
    }
  }, [bloc, formValue]);

  const onFetchMore = useCallback(
    page => {
      if (loading.loading || loading.refreshing || !activities.length) return;
      bloc.onReload(page, R.last(activities)?.createdAt);
      setQueryPage(page);
    },
    [bloc, loading.loading, loading.refreshing, activities],
  );

  useEffect(() => {
    if (selectedNoteType && activities.length > 0) {
      form.controls.types.setValue([selectedNoteType]);
    }
  }, [activities.length, form.controls.types, selectedNoteType]);

  useEffect(() => {
    bloc.onReload(1);
  }, [bloc, orgId, objectId]);

  return (
    <React.Fragment>
      <div>
        {showFilters && (
          <Row style={{ marginLeft: 65 }}>
            <Col>
              <FormSelect items={userItems} type={'multiple'} control={form.controls.users} isClearable />
            </Col>
            <Col>
              <FormSelect items={contactItems} type={'multiple'} control={form.controls.contacts} isClearable />
            </Col>
            <Col>
              <FormSelect items={typeItems} type={'multiple'} control={form.controls.types} isClearable />
            </Col>
          </Row>
        )}

        <ul className="verti-timeline list-unstyled">
          <InfiniteScroll
            style={{ overflow: 'visible' }}
            dataLength={activities.length}
            next={() => onFetchMore(queryPage + 1)}
            hasMore={hasMore}
            loader={<LoadingIndicator />}
            scrollableTarget={scrollableDiv}
            endMessage={<p style={{ textAlign: 'center' }}></p>}
          >
            {activities.map((item, index) => {
              const isFirst = index === 0;

              return (
                <li className={isFirst ? 'event-list active' : 'event-list'} key={'_exp_' + index}>
                  <div className="event-timeline-dot">
                    <i className={isFirst ? 'bx bx-right-arrow-circle bx-fade-right' : 'bx bx-right-arrow-circle'} />
                  </div>

                  {item.reference && (
                    <NoteReferenceItem item={item} contacts={contacts} orgId={orgId!} onDelete={onDelete} onTogglePin={onTogglePin} />
                  )}
                  {!item.reference && item.activityType === ContactActivityType.email && item.email && (
                    <EmailTimeLineItem
                      threadId={item.id}
                      contacts={contacts}
                      email={item.email}
                      orgId={orgId!}
                      onTogglePin={onTogglePinEmail}
                      onReload={() => bloc.onReload()}
                      isPinned={!!item.pinnedAt}
                    />
                  )}
                  {!item.reference && item.activityType === ContactActivityType.ticket && <TicketTimelineItem item={item} />}
                </li>
              );
            })}
          </InfiniteScroll>
        </ul>
      </div>
    </React.Fragment>
  );
});
